import { useQuery, useInfiniteQuery, type UseQueryOptions } from '@tanstack/react-query'
import type { Product, ProductFilters } from '../types/product'
import { productService } from '../mock/services/product'

export function useProducts(
  filters?: ProductFilters,
  options?: Omit<UseQueryOptions<{ products: Product[], total: number }>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['products', filters],
    queryFn: () => productService.getProducts(filters),
    ...options,
  })
}

export function useInfiniteProducts(filters?: ProductFilters) {
  return useInfiniteQuery({
    queryKey: ['products', 'infinite', filters],
    queryFn: ({ pageParam = 1 }) =>
      productService.getProducts({ ...filters, page: pageParam }),
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const hasMore = lastPage.products.length === (filters?.limit || 20)
      return hasMore ? lastPageParam + 1 : undefined
    },
    initialPageParam: 1,
  })
}

export function useProduct(
  id: string,
  options?: Omit<UseQueryOptions<Product | undefined>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getProductById(id),
    enabled: !!id,
    ...options,
  })
}

export function useProductBySlug(
  slug: string,
  options?: Omit<UseQueryOptions<Product | undefined>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['product', 'slug', slug],
    queryFn: () => productService.getProductBySlug(slug),
    enabled: !!slug,
    ...options,
  })
}

export function useTodayDeals(
  options?: Omit<UseQueryOptions<Product[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['deals', 'today'],
    queryFn: () => productService.getTodayDeals(),
    ...options,
  })
}

export function useCategories(
  options?: Omit<UseQueryOptions<any[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => productService.getCategories(),
    ...options,
  })
}

export function useSearchProducts(query: string, limit = 10) {
  return useQuery({
    queryKey: ['products', 'search', query, limit],
    queryFn: () => productService.searchProducts(query, limit),
    enabled: query.length > 0,
  })
}
